import { Link, getRouteApi, useNavigate, useRouter } from '@tanstack/react-router';
import { useReadOnly } from './readOnly.ts';
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import {
  ATLAS_LIMITS,
  area,
  brushShape,
  centroid,
  isRevealed,
  parseFog,
  polygonShape,
  revealedFraction,
  revealsAny,
  formatArea,
  formatInUnit,
  formatMeasure,
  gridSegments,
  mapDistance,
  niceDistance,
  unitDisplay,
  type MapGrid,
  type MapScale,
  type UnitSystem,
  pathLength,
  regionColor,
  MAP_EDIT_LIMITS,
  NEUTRAL_COLOR,
  NEW_CATEGORY_COLOR,
  REGION_MIN_POINTS,
  type CategoryDetails,
  type MapDeletionImpact,
  type MapIcon,
  type MarkerStyle,
  type PinDetails,
  type RegionDetails,
  type ResolvedIcon,
  type MapMarker,
  type MapPoint,
  type MapRegion,
  type MapSummary,
  type MarkerCategory,
} from '@realmery/realm-core';
import { fitView, usePanZoom } from '../canvas/usePanZoom.ts';
import { prepareMapImage, thumbnailOf } from '../canvas/mapImage.ts';
import { CategoryBadge, PinArt, TOKEN_RADIUS, pinLook, useIconUrls } from '../canvas/pins.tsx';
import { IconPicker, IconSwatch } from '../canvas/IconPicker.tsx';
import { useDrawTool } from '../canvas/drawTool.tsx';
import { PIN_FOG_RADIUS, useFogTool } from '../canvas/fogTool.tsx';
import { usePlayerView } from '../canvas/PlayerViewHost.tsx';
import { ColorField, MAP_SWATCHES } from '../canvas/ColorField.tsx';

type PictureUrl = (icon: ResolvedIcon & { kind: 'PICTURE' }) => string;
import { RealmClientError, realms } from '../db/client.ts';
import { realmFile, type AtlasMapView } from '../db/protocol.ts';
import { ImageFileError } from '../editor/imageFiles.ts';
import { settings, useSettings } from '../settings.ts';

/**
 * The Atlas (docs/PARITY-STEP-4-PLAN.md section 4), as the desktop's `AtlasPane` and
 * `AtlasMapView` show it: a gallery of the realm's maps, and a map open with its pins and
 * regions in their categories' colours and icons, the Legend (what's shown), Find, and the
 * Details of the map or of what's picked. Double-click (or Enter on) a pin or region to
 * follow it to the map it opens, else its entry; the breadcrumb climbs back up. Editing
 * comes in slice 3; scale, grid, drawings and fog in 4b.
 */

const galleryRoute = getRouteApi('/_app/realm/$realmKey/atlas');
const mapRoute = getRouteApi('/_app/realm/$realmKey/atlas/$mapId');
const realmRoute = getRouteApi('/_app/realm/$realmKey');

/** `AtlasMapView.MIN_SCALE` and `MAX_SCALE`. */
const MIN_SCALE = 0.05;
const MAX_SCALE = 8;
/** Vertical nudges tried for a region's name when it would collide (`LABEL_NUDGES`), in screen pixels. */
const NUDGES = [0, -34, 34, -60, 60, -86, 86];
/** The room the floating panels take from the map's left and right, in screen pixels. */
const PANEL_ROOM = { legend: 266, details: 286 } as const;
const REGION_LABEL_SIZE = 16;
const PIN_LABEL_SIZE = 12;

const message = (e: unknown, fallback: string) => (e instanceof RealmClientError || e instanceof ImageFileError ? e.message : fallback);
const plural = (n: number, one: string, many: string) => `${n.toLocaleString('en')} ${n === 1 ? one : many}`;
const unlabeled = (label: string) => (label.trim() === '' ? '(unlabeled)' : label);

/** "3 pins, 2 regions and 1 drawing" for a deletion's confirmation. */
function describeImpact(name: string, impact: MapDeletionImpact): string {
  const goes = [
    impact.pins > 0 && plural(impact.pins, 'pin', 'pins'),
    impact.regions > 0 && plural(impact.regions, 'region', 'regions'),
    impact.drawings > 0 && plural(impact.drawings, 'drawing', 'drawings'),
  ].filter((p): p is string => typeof p === 'string');
  const unlinked = [
    impact.openedFrom > 0 && `${plural(impact.openedFrom, 'pin or region', 'pins and regions')} on other maps that open it`,
    impact.cards > 0 && plural(impact.cards, 'Idea Board card', 'Idea Board cards'),
    impact.events > 0 && `${plural(impact.events, 'Chronology event', 'Chronology events')} linked to its pins`,
  ].filter((p): p is string => typeof p === 'string');
  const list = (parts: string[]) => (parts.length <= 1 ? parts.join('') : `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}`);
  const one = impact.pins + impact.regions + impact.drawings === 1;
  return [`Delete “${name}”?`, goes.length > 0 ? ` Its ${list(goes)} ${one ? 'goes' : 'go'} with it.` : '', unlinked.length > 0 ? ` ${list(unlinked)} will no longer be linked to it.` : ''].join('');
}

// ---- the gallery ----

export function AtlasGalleryPage() {
  const maps = galleryRoute.useLoaderData();
  const { realmKey } = galleryRoute.useParams();
  const readOnly = useReadOnly();
  const file = realmFile(realmKey);
  const router = useRouter();
  const navigate = useNavigate();
  const [importing, setImporting] = useState(false);
  const [filter, setFilter] = useState('');
  const [renaming, setRenaming] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<{ id: string; text: string } | null>(null);
  const [error, setError] = useState('');
  const thumbnails = useThumbnailUrls(maps);

  async function run(work: () => Promise<unknown>, fallback: string): Promise<boolean> {
    setError('');
    try {
      await work();
      await router.invalidate();
      return true;
    } catch (e) {
      setError(message(e, fallback));
      return false;
    }
  }

  // Maps imported before thumbnails get theirs made once, as the desktop does (not in a realm shared to read).
  const missing = readOnly ? '' : maps.filter((m) => m.thumbnail === null).map((m) => m.id).join(',');
  useEffect(() => {
    if (missing === '') return;
    let stop = false;
    void (async () => {
      for (const id of missing.split(',')) {
        if (stop) return;
        try {
          const view = await realms.atlasMap(file, id);
          if (view) await realms.storeMapThumbnail(file, id, await thumbnailOf(view.map.imageData));
        } catch {
          // A picture the browser can't read keeps its placeholder.
        }
      }
      if (!stop) await router.invalidate();
    })();
    return () => {
      stop = true;
    };
  }, [missing, file, router]);

  /** Closes only its own confirmation, so a form opened on another map meanwhile stays open. */
  async function deleteMap(id: string) {
    await realms.deleteMap(file, id);
    setDeleting((d) => (d?.id === id ? null : d));
  }

  async function askDelete(m: MapSummary) {
    const remove = () => run(() => deleteMap(m.id), 'The map could not be deleted.');
    if (!settings()['general.confirmDeletes']) return void remove();
    try {
      setDeleting({ id: m.id, text: describeImpact(m.name, await realms.mapDeletionImpact(file, m.id)) });
    } catch (e) {
      setError(message(e, 'The map could not be checked.'));
    }
  }

  const shown = maps.filter((m) => m.name.toLowerCase().includes(filter.trim().toLowerCase()));
  return (
    <article className="document atlas-gallery-page">
      <p className="eyebrow">Atlas</p>
      <h1>Atlas</h1>
      <p className="lead">Your world's maps: a world, a city or a dungeon, with pins and regions linked to the realm's entries, and maps inside maps.</p>
      <div className="actions">
        {!readOnly && (
          <>
            <button type="button" className="primary" onClick={() => setImporting(true)}>
              New map
            </button>
            <Link to="/realm/$realmKey/atlas/icons" params={{ realmKey }} className="button" title="The icons pins can show: built-in, imported, and tokens">
              Icons…
            </Link>
          </>
        )}
        {maps.length > 0 && (
          <label className="atlas-filter">
            <span className="visually-hidden">Filter maps</span>
            <input type="search" value={filter} placeholder="Filter maps" onChange={(e) => setFilter(e.currentTarget.value)} />
          </label>
        )}
      </div>
      {importing && (
        <ImportForm
          onCancel={() => setImporting(false)}
          onImport={(name, image) =>
            run(async () => {
              const prepared = await prepareMapImage(image);
              const s = settings();
              const { id } = await realms.importMap(file, name, prepared.data, prepared.thumbnail, { gridType: s['new.mapGrid'], unit: s['new.mapUnit'] });
              await navigate({ to: '/realm/$realmKey/atlas/$mapId', params: { realmKey, mapId: id }, search: {} });
            }, 'The map could not be imported.')
          }
        />
      )}
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
      {maps.length === 0 ? (
        <p className="meta">No maps yet. Import a map image (PNG, JPEG or WebP) - a world, a continent, a city, a dungeon - to begin.</p>
      ) : shown.length === 0 ? (
        <p className="meta">No maps match “{filter.trim()}”.</p>
      ) : (
        <ul className="atlas-gallery">
          {shown.map((m) => (
            <li key={m.id} className="atlas-card">
              <Link to="/realm/$realmKey/atlas/$mapId" params={{ realmKey, mapId: m.id }} search={{}} className="atlas-card-open">
                {thumbnails.get(m.id) ? <img src={thumbnails.get(m.id)} alt="" /> : <span className="atlas-card-placeholder" aria-hidden="true">🗺</span>}
                <span className="atlas-card-title">{m.name}</span>
              </Link>
              <span className="meta">
                {plural(m.markerCount, 'pin', 'pins')} · {plural(m.regionCount, 'region', 'regions')}
              </span>
              {!readOnly && (
                <span className="actions">
                  <button type="button" className="quiet" onClick={() => setRenaming(m.id)}>
                    Rename…
                  </button>
                  <button type="button" className="quiet" onClick={() => void askDelete(m)}>
                    Delete…
                  </button>
                </span>
              )}
              {renaming === m.id && <RenameForm name={m.name} onCancel={() => setRenaming(null)} onSave={(name) => run(() => realms.renameMap(file, m.id, name).then(() => setRenaming((r) => (r === m.id ? null : r))), 'The map could not be renamed.')} />}
              {deleting?.id === m.id && (
                <Confirm label={`Delete ${m.name}`} text={deleting.text} button="Delete map" onCancel={() => setDeleting(null)} onConfirm={() => run(() => deleteMap(m.id), 'The map could not be deleted.')} />
              )}
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

function useThumbnailUrls(maps: readonly MapSummary[]): ReadonlyMap<string, string> {
  const [urls, setUrls] = useState<ReadonlyMap<string, string>>(new Map());
  useEffect(() => {
    const made = new Map<string, string>();
    for (const m of maps) if (m.thumbnail) made.set(m.id, URL.createObjectURL(new Blob([m.thumbnail as Uint8Array<ArrayBuffer>], { type: 'image/png' })));
    setUrls(made);
    return () => {
      for (const url of made.values()) URL.revokeObjectURL(url);
    };
  }, [maps]);
  return urls;
}

function ImportForm({ onImport, onCancel }: { onImport: (name: string, image: File) => Promise<boolean>; onCancel: () => void }) {
  const [name, setName] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <form
      className="panel"
      aria-label="New map"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        if (!image) return;
        setBusy(true);
        void onImport(name, image).finally(() => setBusy(false));
      }}
    >
      <h3>New map</h3>
      <label>
        Map image (PNG, JPEG or WebP, up to 50 MB)
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          required
          onChange={(e) => {
            const chosen = e.currentTarget.files?.[0] ?? null;
            setImage(chosen);
            if (chosen && name.trim() === '') setName(chosen.name.replace(/\.[^.]+$/, '').slice(0, ATLAS_LIMITS.name));
          }}
        />
      </label>
      <label>
        Name
        <input value={name} maxLength={ATLAS_LIMITS.name} placeholder="e.g. The Northern Continent" onChange={(e) => setName(e.currentTarget.value)} />
      </label>
      <div className="actions">
        <button type="submit" className="primary" disabled={busy || !image}>
          {busy ? 'Importing…' : 'Import map'}
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function RenameForm({ name: initial, onSave, onCancel }: { name: string; onSave: (name: string) => Promise<unknown>; onCancel: () => void }) {
  const [name, setName] = useState(initial);
  return (
    <form
      className="panel"
      aria-label="Rename map"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        void onSave(name);
      }}
    >
      <label>
        Name
        <input value={name} required maxLength={ATLAS_LIMITS.name} autoFocus onChange={(e) => setName(e.currentTarget.value)} />
      </label>
      <div className="actions">
        <button type="submit" className="primary">
          Rename
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---- a map ----

type Picked = { readonly kind: 'pin' | 'region'; readonly id: string } | null;

export function AtlasMapPage() {
  const view = mapRoute.useLoaderData() as AtlasMapView | null;
  const { realmKey } = mapRoute.useParams();
  if (!view) {
    return (
      <article className="document">
        <h1>Map not found</h1>
        <p>
          This map isn't in the realm any more.{' '}
          <Link to="/realm/$realmKey/atlas" params={{ realmKey }}>
            The Atlas
          </Link>
        </p>
      </article>
    );
  }
  return <OpenMap key={view.map.id} view={view} realmKey={realmKey} />;
}

/** What's shown, as the Legend sets it (`AtlasMapView.options`). */
interface Shown {
  readonly hiddenCategories: ReadonlySet<string>;
  readonly regions: boolean;
  readonly labels: boolean;
  readonly gmOnly: boolean;
}
const UNCATEGORIZED = '';

type Tool = 'select' | 'pin' | 'region' | 'measure' | 'calibrate' | 'draw' | 'fog';

/** The tools, as the desktop's `AtlasMapView.Tool` describes them. */
const TOOLS: readonly { tool: Tool; label: string; key: string; hint: string }[] = [
  { tool: 'select', label: '✋ Select', key: 'v', hint: 'Click a pin or region to see and edit it · drag a pin to move it · drag the map to pan' },
  { tool: 'pin', label: '📍 Pin', key: 'p', hint: 'Click the map to place a pin · Esc to cancel' },
  { tool: 'region', label: '⬠ Region', key: 'r', hint: 'Click to add corners · double-click, Enter, or click the first corner to finish · Backspace undoes a corner · Esc cancels' },
  { tool: 'measure', label: '📏 Measure', key: 'm', hint: 'Click to start a route and add stops · double-click or Enter to finish · Backspace undoes a stop · Esc clears it' },
  { tool: 'draw', label: '✏ Draw', key: 'd', hint: 'Draw on the map: pick a shape in the Draw panel' },
  { tool: 'fog', label: '🌫 Fog', key: 'f', hint: 'Fog of war: paint to reveal or hide' },
];
const EXPANDED_KEY = 'realmery.atlas.fullView';
function readExpanded(): boolean {
  try {
    return localStorage.getItem(EXPANDED_KEY) === '1';
  } catch {
    return false;
  }
}
const CALIBRATE_HINT = 'Draw a line over a distance you know, such as the map’s own scale bar: click its start, then its end · Enter to finish · Esc cancels';
/** A click this close to the first corner, in screen pixels, closes a region (`CLOSE_SNAP_PX`). */
const CLOSE_SNAP = 10;
/** A press that moves less than this, in screen pixels, is a click (`CLICK_DRAG_THRESHOLD`). */
const CLICK_DRAG = 5;

function OpenMap({ view, realmKey }: { view: AtlasMapView; realmKey: string }) {
  const { entries } = realmRoute.useLoaderData();
  const readOnly = useReadOnly();
  /** In a realm shared to read: looking and measuring only. */
  const tools = readOnly ? TOOLS.filter((t) => t.tool === 'select' || t.tool === 'measure') : TOOLS;
  const search = mapRoute.useSearch();
  const navigate = useNavigate();
  const router = useRouter();
  const file = realmFile(realmKey);
  const { map, markers, regions, trail } = view;
  const categories = useMemo(() => new Map(view.categories.map((c) => [c.id, c])), [view.categories]);
  const icons = useMemo(() => new Map(view.icons.map((i) => [i.id, i])), [view.icons]);
  const pictureUrl = useIconUrls();
  // On a phone the panels are sheets over the map, so they start closed and take turns.
  const narrow = typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 700px)').matches;
  const [legend, setLegend] = useState(!narrow);
  const [details, setDetails] = useState(!narrow);
  const [shown, setShown] = useState<Shown>({ hiddenCategories: new Set(), regions: true, labels: true, gmOnly: true });
  const [picked, setPicked] = useState<Picked>(search.pin ? { kind: 'pin', id: search.pin } : search.region ? { kind: 'region', id: search.region } : null);
  const [query, setQuery] = useState('');
  const [tool, setTool] = useState<Tool>('select');
  const [draft, setDraft] = useState<readonly MapPoint[]>([]);
  const [hover, setHover] = useState<MapPoint | null>(null);
  const [moved, setMoved] = useState<ReadonlyMap<string, MapPoint>>(new Map());
  /** The category the last pin or region was saved in, for the next new one (`lastUsedCategoryId`). */
  const [lastCategory, setLastCategory] = useState<string | null>(null);
  const [managing, setManaging] = useState(false);
  const [confirming, setConfirming] = useState<Picked>(null);
  const [error, setError] = useState('');
  /** A measured route, kept until the next one starts. */
  const [measured, setMeasured] = useState(false);
  const [mapSettings, setMapSettings] = useState(false);
  /** The line drawn to calibrate the scale, once it's finished, for asking how far it is. */
  const [calibrated, setCalibrated] = useState<number | null>(null);
  const [showGrid, setShowGrid] = useState(true);
  /** The map filling the window under a slim toolbar, remembered in this browser. */
  const [expanded, setExpandedState] = useState(() => readExpanded());
  /** Set when full view comes or goes: the map is fitted again once the canvas has its new size. */
  const refit = useRef(false);
  const setExpanded = (on: boolean) => {
    refit.current = true;
    setExpandedState(on);
    try {
      localStorage.setItem(EXPANDED_KEY, on ? '1' : '0');
    } catch {
      // Private browsing: it's only for this visit.
    }
  };
  const units = useSettings()['atlas.units'];
  const pz = usePanZoom(MIN_SCALE, MAX_SCALE);
  const { box, svg, size, view: v, setView, dragged, handlers, onKeyDown, toDrawing } = pz;
  const { url, width, height, failed } = useMapImage(map.id, map.imageData, map.mimeType);
  const draw = useDrawTool({ file, mapId: map.id, layers: view.layers, drawings: view.drawings, viewScale: v.scale, run, active: tool === 'draw' });
  const fog = useFogTool({ file, mapId: map.id, fog: map.fog, width, height, viewScale: v.scale, run, active: tool === 'fog' });
  /** The tool that takes the pointer and the panel: drawing or fog. */
  const painter = tool === 'draw' ? draw : tool === 'fog' ? fog : null;
  const [showFogTint, setShowFogTint] = useState(true);
  const playerView = usePlayerView();
  /** The column on the right: a panel that's just opened is scrolled into view there. */
  const side = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (tool === 'draw' || tool === 'fog') side.current?.scrollTo({ top: 0 });
  }, [tool]);
  useEffect(() => {
    if (mapSettings) side.current?.querySelector('.atlas-map-settings')?.scrollIntoView({ block: 'nearest' });
  }, [mapSettings]);

  async function run(work: () => Promise<unknown>, fallback: string): Promise<boolean> {
    setError('');
    try {
      await work();
      await router.invalidate();
      return true;
    } catch (e) {
      setError(message(e, fallback));
      await router.invalidate();
      return false;
    }
  }

  // Fits the map between the panels that float over it, where there's room (on a phone they're along the bottom).
  const fit = useCallback(() => {
    if (width === 0) return;
    let left = legend && !narrow ? PANEL_ROOM.legend : 0;
    let right = details && !narrow ? PANEL_ROOM.details : 0;
    // Too little room between them, and the map fills the width, under their edges.
    if (size.width - left - right < size.width * 0.6) left = right = 0;
    const room = { width: Math.max(size.width - left - right, 120), height: size.height };
    const fitted = fitView({ minX: 0, minY: 0, maxX: width, maxY: height }, room, 12, MIN_SCALE, MAX_SCALE);
    setView({ ...fitted, x: fitted.x + left });
  }, [width, height, size, setView, legend, details, narrow]);

  useEffect(() => {
    if (!refit.current) return;
    refit.current = false;
    fit();
  }, [size, fit]);

  /** Centres a point at a scale of at least 1 (`flyTo`). */
  const flyTo = useCallback(
    (p: MapPoint) =>
      setView((now) => {
        const scale = Math.max(now.scale, Math.min(1, MAX_SCALE));
        return { scale, x: size.width / 2 - p.x * scale, y: size.height / 2 - p.y * scale };
      }),
    [size, setView],
  );

  // Fit on opening, unless a link asked for a place on the map.
  const opened = useRef(false);
  useEffect(() => {
    if (width === 0 || opened.current) return;
    opened.current = true;
    const pin = search.pin ? markers.find((m) => m.id === search.pin) : undefined;
    const region = search.region ? regions.find((r) => r.id === search.region) : undefined;
    if (pin) flyTo(pin);
    else if (region) flyTo(centroid(region.points));
    else fit();
  }, [width, fit, flyTo, markers, regions, search.pin, search.region]);

  const categoryKey = (id: string | null) => (id !== null && categories.has(id) ? id : UNCATEGORIZED);
  const pinShown = (m: MapMarker) => (m.visible || shown.gmOnly) && !shown.hiddenCategories.has(categoryKey(m.categoryId));
  const regionShown = (r: MapRegion) => shown.regions && (r.visible || shown.gmOnly) && !shown.hiddenCategories.has(categoryKey(r.categoryId)) && r.points.length >= REGION_MIN_POINTS;
  const at = (m: MapMarker): MapPoint => moved.get(m.id) ?? m;
  const pins = [...markers].filter(pinShown).sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
  const shownRegions = regions.filter(regionShown);
  const screen = (p: MapPoint) => ({ x: v.x + p.x * v.scale, y: v.y + p.y * v.scale });

  useEffect(() => {
    if (!narrow || !picked) return;
    setLegend(false);
    setDetails(true);
  }, [narrow, picked]);

  const pickedPin = picked?.kind === 'pin' ? markers.find((m) => m.id === picked.id) : undefined;
  const pickedRegion = picked?.kind === 'region' ? regions.find((r) => r.id === picked.id) : undefined;

  const follow = (linkedMapId: string | null, linkedEntryId: string | null) => {
    if (linkedMapId !== null && view.maps.some((m) => m.id === linkedMapId)) void navigate({ to: '/realm/$realmKey/atlas/$mapId', params: { realmKey, mapId: linkedMapId }, search: {} });
    else if (linkedEntryId !== null) void navigate({ to: '/realm/$realmKey/entry/$entryId', params: { realmKey, entryId: linkedEntryId }, search: {} });
  };

  const choose = (next: Tool) => {
    setTool(next);
    setDraft([]);
    setHover(null);
    setMeasured(false);
  };

  /** Finishes a measured route (it stays until the next) or the calibration line (which asks how far it is). */
  function finishLine() {
    if (draft.length < 2) return;
    if (tool === 'measure') {
      setMeasured(true);
      setHover(null);
    } else if (tool === 'calibrate') {
      const length = pathLength(draft);
      choose('select');
      if (length > 0) {
        setCalibrated(length);
        setMapSettings(true);
      }
    }
  }

  const inside = (p: MapPoint) => p.x >= 0 && p.y >= 0 && p.x <= width && p.y <= height;

  async function placePin(p: MapPoint) {
    if (!inside(p)) return;
    let id = '';
    if (await run(async () => void ({ id } = await realms.createPin(file, map.id, round(p), lastCategory)), 'The pin could not be placed.')) {
      setPicked({ kind: 'pin', id });
      setDetails(true);
    }
    choose('select');
  }

  async function finishRegion(points: readonly MapPoint[]) {
    if (points.length < REGION_MIN_POINTS) return;
    let id = '';
    if (await run(async () => void ({ id } = await realms.createRegion(file, map.id, points.map(round), lastCategory)), 'The region could not be made.')) {
      setPicked({ kind: 'region', id });
      setDetails(true);
    }
    choose('select');
  }

  function onMapClick(event: React.MouseEvent) {
    if (dragged.current || painter) return;
    const p = toDrawing(event.clientX, event.clientY);
    if (tool === 'pin') return void placePin(p);
    if (tool === 'measure' || tool === 'calibrate') {
      // A finished route stays until a click starts the next.
      if (measured) {
        setMeasured(false);
        return setDraft([p]);
      }
      return setDraft([...draft, p]);
    }
    if (tool === 'region') {
      const first = draft[0];
      if (first && draft.length >= REGION_MIN_POINTS) {
        const s = screen(first);
        const c = screen(p);
        if (Math.hypot(s.x - c.x, s.y - c.y) <= CLOSE_SNAP) return void finishRegion(draft);
      }
      if (inside(p)) setDraft([...draft, p]);
      return;
    }
    setPicked(null);
  }

  function onMapDoubleClick(event: React.MouseEvent) {
    if (tool === 'draw') {
      event.preventDefault();
      return draw.doubleClick(toDrawing(event.clientX, event.clientY));
    }
    if (tool === 'fog') {
      event.preventDefault();
      return fog.doubleClick();
    }
    if (tool !== 'region' && tool !== 'measure' && tool !== 'calibrate') return;
    event.preventDefault();
    // The double-click's own clicks each added a point where it was: one is enough.
    const corners = draft.filter((p, i) => i === 0 || Math.hypot(p.x - draft[i - 1]!.x, p.y - draft[i - 1]!.y) * v.scale > 3);
    if (tool === 'region') return void finishRegion(corners);
    setDraft(corners);
    if (corners.length >= 2) {
      if (tool === 'measure') {
        setMeasured(true);
        setHover(null);
      } else {
        const length = pathLength(corners);
        choose('select');
        if (length > 0) {
          setCalibrated(length);
          setMapSettings(true);
        }
      }
    }
  }

  function askDelete(target: Picked) {
    if (!target) return;
    if (settings()['general.confirmDeletes']) setConfirming(target);
    else void remove(target);
  }

  const remove = (target: NonNullable<Picked>) =>
    run(async () => {
      if (target.kind === 'pin') await realms.deletePin(file, target.id);
      else await realms.deleteRegion(file, target.id);
      setConfirming(null);
      setPicked(null);
    }, 'That could not be deleted.');

  function onMapKey(event: KeyboardEvent) {
    if (painter?.key(event)) return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
    const toolKey = tools.find((t) => t.key === key);
    if (toolKey && !event.shiftKey) {
      event.preventDefault();
      choose(toolKey.tool);
    } else if (key === '[') {
      event.preventDefault();
      setLegend((s) => !s);
    } else if (key === ']') {
      event.preventDefault();
      setDetails((s) => !s);
    } else if (key === 'e' && !event.shiftKey) {
      event.preventDefault();
      setExpanded(!expanded);
    } else if (key === 'Enter' && tool === 'region') {
      event.preventDefault();
      void finishRegion(draft);
    } else if (key === 'Enter' && (tool === 'measure' || tool === 'calibrate')) {
      event.preventDefault();
      finishLine();
    } else if (key === 'Backspace' && (tool === 'region' || tool === 'measure' || tool === 'calibrate')) {
      event.preventDefault();
      setMeasured(false);
      setDraft(draft.slice(0, -1));
    } else if (key === 'Escape') {
      // Cancels what's being drawn, then goes back to Select, then unpicks.
      if (draft.length > 0) setDraft([]);
      else if (tool !== 'select') choose('select');
      else if (picked) setPicked(null);
      else if (expanded) setExpanded(false);
    } else if ((key === 'Delete' || key === 'Backspace') && picked && !readOnly) {
      event.preventDefault();
      askDelete(picked);
    } else onKeyDown(event, fit);
  }

  // ---- dragging a pin (Select) ----
  const press = useRef<{ id: string; pointer: number; startX: number; startY: number; from: MapPoint } | null>(null);

  const placeProps = (kind: 'pin' | 'region', id: string, linkedMapId: string | null, linkedEntryId: string | null, label: string) => ({
    tabIndex: tool === 'select' ? 0 : -1,
    role: 'button',
    'aria-label': `${kind === 'pin' ? 'Pin' : 'Region'}: ${unlabeled(label)}`,
    'aria-pressed': picked?.kind === kind && picked.id === id,
    onPointerDown: (e: React.PointerEvent) => {
      if (tool !== 'select' || e.button !== 0) return;
      e.stopPropagation();
      if (kind !== 'pin' || readOnly) return;
      (e.currentTarget as Element).setPointerCapture(e.pointerId);
      const m = markers.find((x) => x.id === id)!;
      press.current = { id, pointer: e.pointerId, startX: e.clientX, startY: e.clientY, from: at(m) };
    },
    onPointerMove: (e: React.PointerEvent) => {
      const p = press.current;
      if (!p || p.pointer !== e.pointerId) return;
      e.stopPropagation();
      const dx = e.clientX - p.startX;
      const dy = e.clientY - p.startY;
      if (Math.hypot(dx, dy) <= CLICK_DRAG && !moved.has(p.id)) return;
      setMoved((all) => new Map(all).set(p.id, { x: p.from.x + dx / v.scale, y: p.from.y + dy / v.scale }));
    },
    onPointerUp: (e: React.PointerEvent) => {
      const p = press.current;
      if (!p || p.pointer !== e.pointerId) return;
      e.stopPropagation();
      press.current = null;
      const to = moved.get(p.id);
      if (!to || Math.hypot(e.clientX - p.startX, e.clientY - p.startY) <= CLICK_DRAG) return;
      setPicked({ kind: 'pin', id: p.id });
      void run(() => realms.movePin(file, p.id, round(to)), 'The pin could not be moved.').then(() =>
        setMoved((all) => {
          const next = new Map(all);
          next.delete(p.id);
          return next;
        }),
      );
    },
    onClick: (e: React.MouseEvent) => {
      if (tool !== 'select') return;
      e.stopPropagation();
      setPicked({ kind, id });
    },
    onDoubleClick: (e: React.MouseEvent) => {
      if (tool !== 'select') return;
      e.stopPropagation();
      follow(linkedMapId, linkedEntryId);
    },
    onFocus: () => setPicked({ kind, id }),
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        follow(linkedMapId, linkedEntryId);
      }
    },
  });

  const labels = shown.labels ? regionLabelPositions(shownRegions, pins.map((m) => ({ ...m, ...at(m) })), screen) : new Map<string, MapPoint>();
  const hint = tool === 'calibrate' ? CALIBRATE_HINT : painter ? painter.hint : TOOLS.find((t) => t.tool === tool)!.hint;
  const grid = showGrid ? gridSegments(map.grid, width, height) : [];
  const route = tool === 'measure' || tool === 'calibrate' ? [...draft, ...(hover && !measured ? [hover] : [])] : [];
  const confirmingName = confirming ? (confirming.kind === 'pin' ? markers.find((m) => m.id === confirming.id)?.label : regions.find((r) => r.id === confirming.id)?.label) : undefined;

  return (
    <article className={expanded ? 'document atlas-map-page atlas-expanded' : 'document atlas-map-page'}>
      <nav className="eyebrow atlas-trail" aria-label="Maps above this one">
        <Link to="/realm/$realmKey/atlas" params={{ realmKey }}>
          ← Atlas
        </Link>
        {trail.slice(0, -1).map((crumb) => (
          <span key={crumb.id}>
            {'  ›  '}
            <Link to="/realm/$realmKey/atlas/$mapId" params={{ realmKey, mapId: crumb.id }} search={{}}>
              {crumb.name}
            </Link>
          </span>
        ))}
      </nav>
      <h1>{map.name}</h1>
      <div className="actions atlas-toolbar" role="toolbar" aria-label="Map">
        {expanded && (
          <span className="atlas-toolbar-name" title={map.name}>
            {map.name}
          </span>
        )}
        <span className="view-switch" role="group" aria-label="Tool">
          {tools.map((t) => (
            <button key={t.tool} type="button" className={tool === t.tool ? 'tab selected' : 'tab'} aria-pressed={tool === t.tool} title={`${t.hint} (${t.key.toUpperCase()})`} onClick={() => choose(t.tool)}>
              {t.label}
            </button>
          ))}
        </span>
        {playerView.mapId === map.id ? (
          <button type="button" title="Stop showing this map to the players" onClick={playerView.close}>
            📺 Close Player View
          </button>
        ) : (
          <button type="button" title="Open the Player View: a window for a TV or second screen, showing only what players can see. It follows your changes as you make them." onClick={() => playerView.show(map.id)}>
            📺 Show to players
          </button>
        )}
        {!readOnly && <button type="button" title="The map's scale and grid" aria-expanded={mapSettings} onClick={() => setMapSettings(!mapSettings)}>
          ⚙ Map settings…
        </button>}
        <button type="button" aria-pressed={legend} title="Show or hide the Legend panel ([)" onClick={() => setLegend(!legend)}>
          ◧ Legend
        </button>
        <button type="button" aria-pressed={details} title="Show or hide the Details panel (])" onClick={() => setDetails(!details)}>
          Details ◨
        </button>
        <button type="button" aria-pressed={expanded} title={expanded ? 'Back to the page (E or Esc)' : 'Fill the window with the map (E)'} onClick={() => setExpanded(!expanded)}>
          {expanded ? '⤡ Exit full view' : '⤢ Full view'}
        </button>
      </div>
      <p className="meta atlas-hint" role="status">
        {hint}
        {tool === 'region' && draft.length > 0 ? ` · ${plural(draft.length, 'corner', 'corners')}` : ''}
      </p>
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
      {confirming && (
        <Confirm
          label={`Delete ${unlabeled(confirmingName ?? '')}`}
          text={`Delete ${confirming.kind === 'pin' ? 'the pin' : 'the region'} “${unlabeled(confirmingName ?? '')}”?${confirming.kind === 'pin' ? ' Chronology events that name it will no longer do so.' : ''}`}
          button={confirming.kind === 'pin' ? 'Delete pin' : 'Delete region'}
          onCancel={() => setConfirming(null)}
          onConfirm={() => remove(confirming)}
        />
      )}
      {managing && <CategoryManager file={file} view={view} icons={icons} pictureUrl={pictureUrl} run={run} onClose={() => setManaging(false)} />}
      <div className="atlas-stage">
        {legend && (
          <aside className="atlas-panel atlas-legend" aria-label="Legend">
            <Legend view={view} shown={shown} setShown={setShown} icons={icons} pictureUrl={pictureUrl} categoryKey={categoryKey} />
            {view.layers.length > 0 && (
              <>
                <h3 className="atlas-legend-heading">Drawings</h3>
                <ul className="atlas-legend-rows">
                  {[...view.layers].reverse().map((l) => (
                    <li key={l.id}>
                      <label title="Show or hide this drawing layer here (what players see is set in the Draw panel)">
                        <input type="checkbox" checked={!draw.hiddenLayers.has(l.id)} onChange={(e) => draw.setLayerShown(l.id, e.currentTarget.checked)} />
                        <span>{l.name}</span>
                        <span className="meta" title={l.playersCanSee ? 'Players see this layer in the Player View' : 'Only you see this layer'}>
                          {l.playersCanSee ? 'players' : 'GM only'}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </>
            )}
            <label title={map.fog.enabled ? "Shade the parts of the map players can't see yet (you always see through it)" : 'Fog of war is off for this map - pick the Fog tool (F) to start'}>
              <input type="checkbox" checked={showFogTint && map.fog.enabled} disabled={!map.fog.enabled} onChange={(e) => setShowFogTint(e.currentTarget.checked)} /> Fog of war tint
            </label>
            <label title={map.grid.type === 'NONE' ? 'No grid set - see Map settings' : "Show or hide the map's grid"}>
              <input type="checkbox" checked={showGrid && map.grid.type !== 'NONE'} disabled={map.grid.type === 'NONE'} onChange={(e) => setShowGrid(e.currentTarget.checked)} /> Grid
            </label>
            {!readOnly && (
              <>
                <button type="button" title="Add, rename, and recolour the pin and region categories" onClick={() => setManaging(true)}>
                  Manage categories…
                </button>
                <Link to="/realm/$realmKey/atlas/icons" params={{ realmKey }} className="button" title="The icons pins can show: built-in, imported, and tokens">
                  Icons…
                </Link>
              </>
            )}
            <Find
              query={query}
              setQuery={setQuery}
              markers={markers.filter(pinShown)}
              regions={regions.filter((r) => (r.visible || shown.gmOnly) && r.points.length >= REGION_MIN_POINTS)}
              onPick={(kind, id, place) => {
                setPicked({ kind, id });
                flyTo(place);
              }}
            />
          </aside>
        )}
        <div className="graph-canvas atlas-canvas" ref={box}>
          <div className="graph-zoom" role="group" aria-label="Zoom">
            <button type="button" onClick={() => pz.zoomAt(size.width / 2, size.height / 2, 0.8)} aria-label="Zoom out">
              −
            </button>
            <button type="button" onClick={() => pz.zoomAt(size.width / 2, size.height / 2, 1.25)} aria-label="Zoom in">
              +
            </button>
            <button type="button" onClick={fit} aria-label="Fit the map to the window">
              ⤢
            </button>
          </div>
          <svg
            ref={svg}
            role="group"
            aria-label={`${map.name}: ${plural(pins.length, 'pin', 'pins')} and ${plural(shownRegions.length, 'region', 'regions')}. Drag or use the arrow keys to move, + and − to zoom, 0 to fit. V, P and R choose a tool. Tab to a pin or region to pick it; Enter follows it.`}
            tabIndex={0}
            className={`atlas-tool-${tool}`}
            {...handlers}
            onPointerDown={(e) => {
              if (painter?.pointerDown(e, toDrawing(e.clientX, e.clientY))) return;
              handlers.onPointerDown(e);
            }}
            onPointerUp={(e) => {
              if (painter?.pointerUp(e, toDrawing(e.clientX, e.clientY))) return;
              handlers.onPointerUp(e);
            }}
            onPointerMove={(e) => {
              if (painter?.pointerMove(e, toDrawing(e.clientX, e.clientY))) return;
              handlers.onPointerMove(e);
              if ((tool === 'region' || tool === 'measure' || tool === 'calibrate') && !measured) setHover(toDrawing(e.clientX, e.clientY));
            }}
            onClick={onMapClick}
            onDoubleClick={onMapDoubleClick}
            onKeyDown={onMapKey}
          >
            <g transform={`translate(${v.x} ${v.y}) scale(${v.scale})`}>
              {url && width > 0 && <image href={url} width={width} height={height} className="atlas-image" />}
              {grid.length > 0 && (
                <clipPath id="atlas-image-edge">
                  <rect width={width} height={height} />
                </clipPath>
              )}
              {grid.length > 0 && (
                <path
                  clipPath="url(#atlas-image-edge)"
                  d={grid.map(([x1, y1, x2, y2]) => `M${x1},${y1}L${x2},${y2}`).join('')}
                  fill="none"
                  stroke={map.grid.colorHex}
                  strokeOpacity={map.grid.opacity}
                  strokeWidth={1}
                  vectorEffect="non-scaling-stroke"
                  pointerEvents="none"
                />
              )}
              {shownRegions.map((r) => {
                const color = regionColor(r, categories.get(r.categoryId ?? ''));
                const on = picked?.kind === 'region' && picked.id === r.id;
                return (
                  <g key={r.id} {...placeProps('region', r.id, r.linkedMapId, r.linkedEntryId, r.label)} className={on ? 'atlas-region selected' : 'atlas-region'}>
                    <title>{tooltip(r.label, r.notes, r.visible)}</title>
                    <polygon
                      points={r.points.map((p) => `${p.x},${p.y}`).join(' ')}
                      fill={color}
                      fillOpacity={r.visible ? 0.22 : 0.12}
                      stroke={color}
                      strokeWidth={on ? 4 : 2}
                      strokeDasharray={r.visible ? undefined : '6 4'}
                      vectorEffect="non-scaling-stroke"
                    />
                  </g>
                );
              })}
              {draw.art}
              {fog.art(showFogTint)}
            </g>
            {/* Names and pins keep their size on screen, as on the desktop. */}
            {[...labels].map(([id, place]) => (
              <text key={id} x={place.x} y={place.y} fontSize={REGION_LABEL_SIZE} textAnchor="middle" className="atlas-label atlas-region-label">
                {shownRegions.find((r) => r.id === id)!.label}
              </text>
            ))}
            {pins.map((m) => {
              const s = screen(at(m));
              const look = pinLook(m, categories, icons);
              const on = picked?.kind === 'pin' && picked.id === m.id;
              return (
                <g key={m.id} transform={`translate(${s.x} ${s.y})`} opacity={m.visible ? 1 : 0.6} {...placeProps('pin', m.id, m.linkedMapId, m.linkedEntryId, m.label)} className={on ? 'atlas-pin selected' : 'atlas-pin'}>
                  <title>{tooltip(m.label, m.notes, m.visible)}</title>
                  <PinArt marker={m} look={look} pictureUrl={pictureUrl} selected={on} />
                  {shown.labels && m.label.trim() !== '' && (
                    <text y={m.style === 'TOKEN' ? TOKEN_RADIUS + 12 : 16} fontSize={PIN_LABEL_SIZE} textAnchor="middle" className="atlas-label">
                      {m.label}
                    </text>
                  )}
                </g>
              );
            })}
            {tool === 'region' && draft.length > 0 && <RegionDraft corners={[...draft.map(screen)]} hover={hover ? screen(hover) : null} />}
            {route.length > 0 && (
              <Route points={route.map(screen)} readout={tool === 'calibrate' ? `${formatMeasure(Math.round(pathLength(route)))} px` : mapDistance(map.scale, pathLength(route), units)} />
            )}
          </svg>
          <ScaleBar scale={map.scale} viewScale={v.scale} units={units} />
          {failed && <p className="board-empty notice-error">This map's picture couldn't be read.</p>}
        </div>
        {/* The tool's panel, Map settings and Details, one under another, so any of them can be open together. */}
        <div className="atlas-side" ref={side}>
          {tool === 'draw' && <aside className="atlas-panel atlas-details" aria-label="Draw panel">{draw.panel}</aside>}
          {tool === 'fog' && <aside className="atlas-panel atlas-details" aria-label="Fog panel">{fog.panel}</aside>}
          {mapSettings && (
            <MapSettingsForm
              key={`${map.id}:${calibrated ?? ''}`}
              scale={map.scale}
              grid={map.grid}
              width={width}
              calibrated={calibrated}
              onCalibrate={() => {
                setMapSettings(false);
                setCalibrated(null);
                choose('calibrate');
              }}
              onCancel={() => {
                setMapSettings(false);
                setCalibrated(null);
              }}
              onSave={(scale, grid) =>
                run(() => realms.updateMapSettings(file, map.id, scale, grid), "The map's settings could not be saved.").then((ok) => {
                  if (ok) {
                    setMapSettings(false);
                    setCalibrated(null);
                  }
                  return ok;
                })
              }
            />
          )}
          {details && (
            <aside className="atlas-panel atlas-details" aria-label="Details">
              {(pickedPin || pickedRegion) && readOnly ? (
                <PlaceView
                  key={`${picked!.kind}:${picked!.id}`}
                  place={(pickedPin ?? pickedRegion)!}
                  kind={pickedPin ? 'pin' : 'region'}
                  view={view}
                  entries={entries}
                  realmKey={realmKey}
                  onCenter={() => flyTo(pickedPin ? at(pickedPin) : centroid(pickedRegion!.points))}
                />
              ) : pickedPin || pickedRegion ? (
                <PlaceEditor
                  key={`${picked!.kind}:${picked!.id}`}
                  pin={pickedPin}
                  region={pickedRegion}
                  view={view}
                  entries={entries}
                  realmKey={realmKey}
                  icons={icons}
                  pictureUrl={pictureUrl}
                  onCenter={() => flyTo(pickedPin ? at(pickedPin) : centroid(pickedRegion!.points))}
                  onDelete={() => askDelete(picked)}
                  onNewCategory={() => setManaging(true)}
                  fogControls={
                    fog.enabled ? (
                      <FogControls
                        what={pickedPin ? 'pin' : 'region'}
                        revealed={pickedPin ? isRevealed(true, fog.revealed, pickedPin) : revealsAny(true, fog.revealed, pickedRegion!.points)}
                        onApply={(hide) => fog.apply(pickedPin ? brushShape([pickedPin], PIN_FOG_RADIUS) : polygonShape(pickedRegion!.points), hide)}
                      />
                    ) : null
                  }
                  onSave={(details) =>
                    run(async () => {
                      if (pickedPin) await realms.updatePin(file, pickedPin.id, details as PinDetails);
                      else await realms.updateRegion(file, pickedRegion!.id, details as RegionDetails);
                      setLastCategory(details.categoryId);
                    }, 'That could not be saved.')
                  }
                />
              ) : (
                <MapDetails view={view} width={width} height={height} units={units} />
              )}
            </aside>
          )}
        </div>
      </div>
    </article>
  );
}

/** Map pixels to two places, as the desktop stores a region's corners. */
const round = (p: MapPoint): MapPoint => ({ x: Math.round(p.x * 100) / 100, y: Math.round(p.y * 100) / 100 });

/** The region being drawn: its corners, the line to the pointer, and the first corner larger, to close on. */
function RegionDraft({ corners, hover }: { corners: readonly MapPoint[]; hover: MapPoint | null }) {
  const line = [...corners, ...(hover ? [hover] : [])];
  return (
    <g className="atlas-draft" pointerEvents="none">
      {line.length >= 3 && <polygon points={line.map((p) => `${p.x},${p.y}`).join(' ')} className="atlas-draft-fill" />}
      <polyline points={line.map((p) => `${p.x},${p.y}`).join(' ')} className="atlas-draft-line" />
      {corners.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={i === 0 && corners.length >= REGION_MIN_POINTS ? 6 : 4} className={i === 0 ? 'atlas-draft-first' : 'atlas-draft-corner'} />
      ))}
    </g>
  );
}

/** The map's picture as a URL, with its size once the browser has read it. */
function useMapImage(mapId: string, data: Uint8Array, mimeType: string) {
  // Read again only for another map: an edit reloads the view, with the same picture.
  const latest = useRef(data);
  latest.current = data;
  const [state, setState] = useState<{ url: string | null; width: number; height: number; failed: boolean }>({ url: null, width: 0, height: 0, failed: false });
  useEffect(() => {
    const url = URL.createObjectURL(new Blob([latest.current as Uint8Array<ArrayBuffer>], { type: mimeType }));
    const image = new Image();
    let live = true;
    image.onload = () => live && setState({ url, width: image.naturalWidth, height: image.naturalHeight, failed: false });
    image.onerror = () => live && setState({ url: null, width: 0, height: 0, failed: true });
    image.src = url;
    return () => {
      live = false;
      URL.revokeObjectURL(url);
    };
  }, [mapId, mimeType]);
  return state;
}

function tooltip(label: string, notes: string, visible: boolean): string {
  const text = unlabeled(label) + (visible ? '' : ' (GM only)');
  return notes.trim() === '' ? text : `${text}\n${notes}`;
}

/**
 * Where each region's name goes on screen (`MapExport.regionLabelPositions`, with pins at
 * their design size): at its centroid, left out where a pin of the same name sits right
 * there, otherwise nudged up or down until it clears every pin, pin name and region name.
 */
function regionLabelPositions(regions: readonly MapRegion[], pins: readonly MapMarker[], screen: (p: MapPoint) => MapPoint): Map<string, MapPoint> {
  const box = (x: number, y: number, label: string, size: number) => {
    const half = label.length * size * 0.29;
    return [x - half, y - size * 0.75, x + half, y + size * 0.35] as const;
  };
  const occupied: (readonly [number, number, number, number])[] = [];
  for (const m of pins) {
    const p = screen(m);
    occupied.push([p.x - 13, p.y - 35, p.x + 13, p.y]);
    if (m.label.trim() !== '') occupied.push(box(p.x, p.y + 16, m.label, PIN_LABEL_SIZE));
  }
  const hits = (a: readonly number[], b: readonly number[]) => a[0]! < b[2]! && b[0]! < a[2]! && a[1]! < b[3]! && b[1]! < a[3]!;
  const placed = new Map<string, MapPoint>();
  for (const r of regions) {
    if (r.label.trim() === '') continue;
    const c = screen(centroid(r.points));
    if (pins.some((m) => m.label.toLowerCase() === r.label.toLowerCase() && Math.hypot(screen(m).x - c.x, screen(m).y - c.y) < 70)) continue;
    for (const nudge of NUDGES) {
      const b = box(c.x, c.y + nudge, r.label, REGION_LABEL_SIZE);
      if (!occupied.some((o) => hits(o, b))) {
        occupied.push(b);
        placed.set(r.id, { x: c.x, y: c.y + nudge });
        break;
      }
    }
  }
  return placed;
}

function Legend({
  view,
  shown,
  setShown,
  icons,
  pictureUrl,
  categoryKey,
}: {
  view: AtlasMapView;
  shown: Shown;
  setShown: (s: Shown) => void;
  icons: ReadonlyMap<string, import('@realmery/realm-core').MapIcon>;
  pictureUrl: Parameters<typeof CategoryBadge>[0]['pictureUrl'];
  categoryKey: (id: string | null) => string;
}) {
  const counts = new Map<string, number>();
  for (const p of [...view.markers, ...view.regions]) counts.set(categoryKey(p.categoryId), (counts.get(categoryKey(p.categoryId)) ?? 0) + 1);
  const toggle = (key: string, on: boolean) => {
    const hidden = new Set(shown.hiddenCategories);
    if (on) hidden.delete(key);
    else hidden.add(key);
    setShown({ ...shown, hiddenCategories: hidden });
  };
  const rows: { key: string; category: MarkerCategory | undefined; name: string }[] = view.categories.map((c) => ({ key: c.id, category: c, name: c.name }));
  if ((counts.get(UNCATEGORIZED) ?? 0) > 0 || view.categories.length === 0) rows.push({ key: UNCATEGORIZED, category: undefined, name: 'Uncategorized' });
  return (
    <>
      <h2>Legend</h2>
      <ul className="atlas-legend-rows">
        {rows.map((row) => (
          <li key={row.key}>
            <label title="Show or hide this category's pins and regions">
              <input type="checkbox" checked={!shown.hiddenCategories.has(row.key)} onChange={(e) => toggle(row.key, e.currentTarget.checked)} />
              <CategoryBadge category={row.category} icons={icons} pictureUrl={pictureUrl} />
              <span>{row.name}</span>
              <span className="meta">{counts.get(row.key) ?? 0}</span>
            </label>
          </li>
        ))}
      </ul>
      <label title="Show or hide the regions on this map">
        <input type="checkbox" checked={shown.regions} onChange={(e) => setShown({ ...shown, regions: e.currentTarget.checked })} /> Regions ({view.regions.length})
      </label>
      <label title="Show or hide the names beside pins and regions">
        <input type="checkbox" checked={shown.labels} onChange={(e) => setShown({ ...shown, labels: e.currentTarget.checked })} /> Labels
      </label>
      <label title={'Pins and regions marked "Hidden from players" are drawn faded and dashed'}>
        <input type="checkbox" checked={shown.gmOnly} onChange={(e) => setShown({ ...shown, gmOnly: e.currentTarget.checked })} /> GM-only pins &amp; regions
      </label>
    </>
  );
}

function Find({ query, setQuery, markers, regions, onPick }: { query: string; setQuery: (q: string) => void; markers: readonly MapMarker[]; regions: readonly MapRegion[]; onPick: (kind: 'pin' | 'region', id: string, at: MapPoint) => void }) {
  const q = query.trim().toLowerCase();
  const results = [
    ...markers.map((m) => ({ kind: 'pin' as const, id: m.id, label: m.label, at: { x: m.x, y: m.y } as MapPoint })),
    ...regions.map((r) => ({ kind: 'region' as const, id: r.id, label: r.label, at: centroid(r.points) })),
  ]
    .filter((x) => q === '' || x.label.toLowerCase().includes(q))
    .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
  return (
    <>
      <h2>Find</h2>
      <label>
        <span className="visually-hidden">Search pins and regions</span>
        <input type="search" value={query} placeholder="Search pins and regions" onChange={(e) => setQuery(e.currentTarget.value)} />
      </label>
      {results.length === 0 ? (
        <p className="meta">{q === '' ? 'Nothing here yet' : 'Nothing matches.'}</p>
      ) : (
        <ul className="atlas-find" aria-label="Pins and regions">
          {results.map((x) => (
            <li key={`${x.kind}:${x.id}`}>
              <button type="button" className="quiet" title="Fly to it" onClick={() => onPick(x.kind, x.id, x.at)}>
                {x.kind === 'pin' ? '📍' : '⬠'} {unlabeled(x.label)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function MapDetails({ view, width, height, units }: { view: AtlasMapView; width: number; height: number; units: UnitSystem }) {
  const { map } = view;
  const hidden = view.markers.filter((m) => !m.visible).length + view.regions.filter((r) => !r.visible).length;
  return (
    <>
      <h2>Map</h2>
      <dl className="atlas-facts">
        <dt>Size</dt>
        <dd>{width > 0 ? `${width.toLocaleString('en')} × ${height.toLocaleString('en')} px` : '…'}</dd>
        <dt>Contents</dt>
        <dd>
          {plural(view.markers.length, 'pin', 'pins')}, {plural(view.regions.length, 'region', 'regions')}
          {hidden > 0 ? ` (${hidden.toLocaleString('en')} GM only)` : ''}
        </dd>
        <dt>Scale</dt>
        <dd>{map.scale.unitsPerPixel > 0 && width > 0 ? `The map is ${mapDistance(map.scale, width, units)} across` : 'None set'}</dd>
        <dt>Grid</dt>
        <dd>
          {map.grid.type === 'NONE'
            ? 'None'
            : `${map.grid.type === 'HEX' ? 'Hex' : 'Square'} grid, ${Math.round(map.grid.cellSize)} px cells${map.scale.unitsPerPixel > 0 ? ` (${mapDistance(map.scale, map.grid.cellSize, units)})` : ''}`}
        </dd>
        <dt>Fog of war</dt>
        <dd>{map.fog.enabled ? (width > 0 ? `On, ${Math.round(revealedFraction(true, parseFog(map.fog.revealed), width, height) * 100)}% revealed` : 'On') : 'Off'}</dd>
      </dl>
      <p className="meta">Pick a pin or region to see its details and edit them.</p>
    </>
  );
}

function RegionMeasures({ region, scale, units }: { region: MapRegion; scale: MapScale; units: UnitSystem }) {
  const closed = [...region.points, region.points[0]!];
  const square = area(region.points);
  return (
    <dl className="atlas-facts">
      <dt>Area</dt>
      <dd>{scale.unitsPerPixel > 0 ? formatArea(square * scale.unitsPerPixel * scale.unitsPerPixel, scale.unit, units) : `${formatMeasure(square)} sq px`}</dd>
      <dt>Border</dt>
      <dd>{mapDistance(scale, pathLength(closed), units)}</dd>
    </dl>
  );
}

/** A pin's or region's fog: whether players can see it, and ☀ Reveal to players / 🌫 Cover with fog (`fogControls`). */
function FogControls({ what, revealed, onApply }: { what: 'pin' | 'region'; revealed: boolean; onApply: (hide: boolean) => void }) {
  return (
    <div className="atlas-fog-controls">
      <p className="meta">{revealed ? `☀ Players can see this ${what}` : `🌫 Under fog - players can't see this ${what} yet`}</p>
      <div className="actions">
        <button type="button" title={`Clear the fog over this ${what} so players can see it`} onClick={() => onApply(false)}>
          ☀ Reveal to players
        </button>
        <button type="button" title={`Put the fog back over this ${what}`} onClick={() => onApply(true)}>
          🌫 Cover with fog
        </button>
      </div>
    </div>
  );
}

/** A measured route on screen, with its length beside its last stop. */
function Route({ points, readout }: { points: readonly MapPoint[]; readout: string }) {
  const last = points.at(-1)!;
  return (
    <g className="atlas-route" pointerEvents="none">
      <polyline points={points.map((p) => `${p.x},${p.y}`).join(' ')} className="atlas-route-line" />
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={4} className="atlas-route-stop" />
      ))}
      {points.length >= 2 && (
        <text x={last.x + 12} y={last.y - 12} className="atlas-label atlas-route-readout" role="status">
          {readout}
        </text>
      )}
    </g>
  );
}

/** The scale bar in the map's corner: a round distance about 140 pixels long at this zoom (`updateScaleBar`). */
function ScaleBar({ scale, viewScale, units }: { scale: MapScale; viewScale: number; units: UnitSystem }) {
  if (!(scale.unitsPerPixel > 0)) return null;
  const perScreenPixel = scale.unitsPerPixel / viewScale;
  const display = unitDisplay(140 * perScreenPixel, scale.unit, units);
  const shown = perScreenPixel * display.factor;
  const distance = niceDistance(140 * shown);
  if (!(distance > 0)) return null;
  const pixels = distance / shown;
  const label = formatInUnit(distance, display);
  return (
    <div className="atlas-scale-bar" aria-label={`Scale: ${label}`} role="img">
      <span className="atlas-scale-line" style={{ width: `${pixels}px` }} />
      <span>{label}</span>
    </div>
  );
}

/**
 * Map settings...: the scale ("the map is 1,200 miles across", or calibrated from a line
 * drawn over a known distance) and a square or hex grid (`showMapSettings`).
 */
function MapSettingsForm({
  scale,
  grid,
  width,
  calibrated,
  onCalibrate,
  onSave,
  onCancel,
}: {
  scale: MapScale;
  grid: MapGrid;
  width: number;
  /** The length of a calibration line just drawn, in map pixels. */
  calibrated: number | null;
  onCalibrate: () => void;
  onSave: (scale: MapScale, grid: MapGrid) => Promise<boolean>;
  onCancel: () => void;
}) {
  const s = settings();
  const defaultUnit = s['new.mapUnit'].trim() || (s['atlas.units'] === 'metric' ? 'km' : 'miles');
  const across = scale.unitsPerPixel > 0 && width > 0 ? formatMeasure(width * scale.unitsPerPixel).replace(/,/g, '') : '';
  const [mapWidth, setMapWidth] = useState(across);
  const [lineDistance, setLineDistance] = useState('');
  const [unit, setUnit] = useState(scale.unit || defaultUnit);
  const [gridType, setGridType] = useState(grid.type);
  const [cell, setCell] = useState(formatMeasure(grid.cellSize).replace(/,/g, ''));
  const [gridColor, setGridColor] = useState(/^#[0-9A-Fa-f]{6}$/.test(grid.colorHex) ? grid.colorHex : '#000000');
  const [opacity, setOpacity] = useState(Math.max(0.05, grid.opacity));
  const positive = (text: string) => {
    const n = Number(text.replace(/,/g, '').trim());
    return Number.isFinite(n) && n > 0 ? n : 0;
  };
  const perPixel = calibrated !== null ? (positive(lineDistance) > 0 ? positive(lineDistance) / calibrated : 0) : width > 0 && positive(mapWidth) > 0 ? positive(mapWidth) / width : 0;
  const cellSize = positive(cell) || grid.cellSize;
  return (
    <form
      className="atlas-panel atlas-map-settings"
      aria-label="Map settings"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        void onSave({ unitsPerPixel: perPixel, unit: perPixel > 0 ? unit : '' }, { type: gridType, cellSize, colorHex: gridColor, opacity });
      }}
    >
      <h2>Map settings</h2>
      <h3>Distance scale</h3>
      {calibrated !== null ? (
        <div className="row wrap">
          <label>
            {`That line is ${formatMeasure(Math.round(calibrated))} px. How far is it?`}
            <input inputMode="decimal" value={lineDistance} required autoFocus placeholder="e.g. 100" onChange={(e) => setLineDistance(e.currentTarget.value)} />
          </label>
          <label>
            Unit
            <input value={unit} maxLength={40} onChange={(e) => setUnit(e.currentTarget.value)} />
          </label>
        </div>
      ) : (
        <>
          <div className="row wrap">
            <label>
              The map is this wide
              <input inputMode="decimal" value={mapWidth} placeholder="e.g. 1200" onChange={(e) => setMapWidth(e.currentTarget.value)} />
            </label>
            <label>
              Unit
              <input value={unit} maxLength={40} onChange={(e) => setUnit(e.currentTarget.value)} />
            </label>
          </div>
          <p className="meta">Leave the width blank for no scale (the ruler then measures in pixels).</p>
          <button type="button" onClick={onCalibrate}>
            📏 Calibrate by drawing a line…
          </button>
        </>
      )}
      <h3>Grid overlay</h3>
      <div className="row wrap">
        <label>
          Grid
          <select value={gridType} onChange={(e) => setGridType(e.currentTarget.value as MapGrid['type'])}>
            <option value="NONE">No grid</option>
            <option value="SQUARE">Square</option>
            <option value="HEX">Hex</option>
          </select>
        </label>
        <label>
          Cell size (px)
          <input inputMode="decimal" value={cell} disabled={gridType === 'NONE'} onChange={(e) => setCell(e.currentTarget.value)} />
        </label>
        {gridType !== 'NONE' && perPixel > 0 && <span className="meta">≈ {formatMeasure(cellSize * perPixel)} {unit.trim()} per cell</span>}
      </div>
      {gridType !== 'NONE' && (
        <>
          <ColorField label="Grid colour" value={gridColor} swatches={GRID_SWATCHES} onChange={setGridColor} />
          <label>
            Opacity
            <input type="range" min={0.05} max={1} step={0.05} value={opacity} onChange={(e) => setOpacity(Number(e.currentTarget.value))} />
          </label>
        </>
      )}
      <div className="actions">
        <button type="submit" className="primary">
          Save
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** For grid lines: dark and light, so a grid can be seen on any map. */
const GRID_SWATCHES = [
  { hex: '#000000', name: 'Black' },
  { hex: '#3A2C1E', name: 'Sepia' },
  { hex: '#5B6FB5', name: 'Blue' },
  { hex: '#B23A2F', name: 'Red' },
  { hex: '#FFFFFF', name: 'White' },
];

/**
 * The Details panel for a picked pin or region, as the desktop's inspector: its name,
 * category (with + for a new one), the entry and map it links to, notes, Hidden from
 * players, a pin's icon and Show as, a region's own colour; Save, Center and Delete.
 */
/** A pin or region in a realm shared to read: what it is and links to, with nothing to change. */
function PlaceView({ place, kind, view, entries, realmKey, onCenter }: {
  place: MapMarker | MapRegion;
  kind: 'pin' | 'region';
  view: AtlasMapView;
  entries: readonly { id: string; title: string }[];
  realmKey: string;
  onCenter: () => void;
}) {
  const entry = place.linkedEntryId ? entries.find((e) => e.id === place.linkedEntryId) : undefined;
  const opens = place.linkedMapId ? view.maps.find((m) => m.id === place.linkedMapId) : undefined;
  const category = place.categoryId ? view.categories.find((c) => c.id === place.categoryId) : undefined;
  return (
    <div className="atlas-place-view">
      <h2>{unlabeled(place.label)}</h2>
      <p className="meta">
        {kind === 'pin' ? 'Pin' : 'Region'}
        {category && ` · ${category.name}`}
      </p>
      {(entry || opens) && (
        <p className="atlas-place-links">
          {entry && (
            <Link to="/realm/$realmKey/entry/$entryId" params={{ realmKey, entryId: entry.id }} search={{}}>
              {entry.title} →
            </Link>
          )}
          {opens && (
            <Link to="/realm/$realmKey/atlas/$mapId" params={{ realmKey, mapId: opens.id }} search={{}}>
              Open {opens.name} →
            </Link>
          )}
        </p>
      )}
      {place.notes && <p className="pre-line">{place.notes}</p>}
      <div className="actions">
        <button type="button" onClick={onCenter}>
          Center
        </button>
      </div>
    </div>
  );
}

function PlaceEditor({
  pin,
  region,
  view,
  entries,
  realmKey,
  icons,
  pictureUrl,
  onSave,
  onCenter,
  onDelete,
  onNewCategory,
  fogControls,
}: {
  pin: MapMarker | undefined;
  region: MapRegion | undefined;
  view: AtlasMapView;
  entries: readonly { id: string; title: string; typeName: string }[];
  realmKey: string;
  icons: ReadonlyMap<string, MapIcon>;
  pictureUrl: PictureUrl;
  onSave: (details: PinDetails | RegionDetails) => Promise<boolean>;
  onCenter: () => void;
  onDelete: () => void;
  onNewCategory: () => void;
  /** The fog's status and ☀ and 🌫 buttons, when the map has fog. */
  fogControls: ReactNode;
}) {
  const place = (pin ?? region)!;
  const kind = pin ? 'Pin' : 'Region';
  const units = useSettings()['atlas.units'];
  const [label, setLabel] = useState(place.label);
  const [categoryId, setCategoryId] = useState(place.categoryId ?? '');
  const [entryId, setEntryId] = useState(place.linkedEntryId ?? '');
  const [mapId, setMapId] = useState(place.linkedMapId ?? '');
  const [notes, setNotes] = useState(place.notes);
  const [hidden, setHidden] = useState(!place.visible);
  const [icon, setIcon] = useState(pin?.icon ?? '');
  const [style, setStyle] = useState<MarkerStyle>(pin?.style ?? 'PIN');
  const [ownColor, setOwnColor] = useState(region?.colorHex ?? null);
  const [choosing, setChoosing] = useState(false);
  const [saved, setSaved] = useState(false);
  const category = view.categories.find((c) => c.id === categoryId);
  const color = category?.colorHex ?? NEUTRAL_COLOR;
  const entry = place.linkedEntryId ? entries.find((e) => e.id === place.linkedEntryId) : undefined;
  const opens = place.linkedMapId ? view.maps.find((m) => m.id === place.linkedMapId) : undefined;
  const common = { label, categoryId: categoryId || null, notes, visible: !hidden, linkedEntryId: entryId || null, linkedMapId: mapId || null };
  return (
    <form
      aria-label={`${kind} details`}
      className="atlas-place-form"
      onChange={() => setSaved(false)}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        void onSave(pin ? { ...common, icon, style } : { ...common, colorHex: ownColor }).then((ok) => setSaved(ok));
      }}
    >
      <p className="eyebrow">
        {kind} on {view.map.name}
      </p>
      <label>
        Name
        <input value={label} maxLength={MAP_EDIT_LIMITS.label} placeholder="(unlabeled)" onChange={(e) => setLabel(e.currentTarget.value)} />
      </label>
      <div className="atlas-category-row">
        <label>
          Category
          <select value={categoryId} onChange={(e) => setCategoryId(e.currentTarget.value)}>
            <option value="">Uncategorized</option>
            {view.categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <button type="button" title="A new category" aria-label="New category" onClick={onNewCategory}>
          +
        </button>
      </div>
      <label>
        Entry
        <select value={entryId} onChange={(e) => setEntryId(e.currentTarget.value)}>
          <option value="">(none)</option>
          {entries.map((e) => (
            <option key={e.id} value={e.id}>
              {e.title} ({e.typeName})
            </option>
          ))}
        </select>
      </label>
      <label>
        Map
        <select value={mapId} onChange={(e) => setMapId(e.currentTarget.value)}>
          <option value="">(none)</option>
          {view.maps
            .filter((m) => m.id !== view.map.id)
            .map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
        </select>
      </label>
      {fogControls}
      {(entry || opens) && (
        <p className="atlas-place-links">
          {entry && (
            <Link to="/realm/$realmKey/entry/$entryId" params={{ realmKey, entryId: entry.id }} search={{}}>
              {entry.title} →
            </Link>
          )}
          {opens && (
            <Link to="/realm/$realmKey/atlas/$mapId" params={{ realmKey, mapId: opens.id }} search={{}}>
              Open {opens.name} →
            </Link>
          )}
        </p>
      )}
      <label>
        Notes
        <textarea value={notes} rows={4} maxLength={MAP_EDIT_LIMITS.notes} onChange={(e) => setNotes(e.currentTarget.value)} />
      </label>
      <label className="check">
        <input type="checkbox" checked={hidden} onChange={(e) => setHidden(e.currentTarget.checked)} /> Hidden from players (GM only)
      </label>
      {pin && (
        <>
          <div className="atlas-icon-row">
            <span>Icon</span>
            <IconSwatch value={icon.trim() === '' ? category?.icon ?? '' : icon} color={color} icons={icons} pictureUrl={pictureUrl} />
            <span className="meta">{icon.trim() === '' ? "Its category's" : ''}</span>
            <button type="button" onClick={() => setChoosing(!choosing)}>
              Choose…
            </button>
          </div>
          {choosing && (
            <IconPicker
              value={icon}
              forPin
              color={color}
              icons={view.icons}
              pictureUrl={pictureUrl}
              onCancel={() => setChoosing(false)}
              onPick={(chosen) => {
                setIcon(chosen);
                setChoosing(false);
                setSaved(false);
              }}
            />
          )}
          <fieldset className="atlas-show-as">
            <legend>Show as</legend>
            {(['PIN', 'TOKEN'] as const).map((s) => (
              <label key={s} className="check">
                <input type="radio" name="show-as" checked={style === s} onChange={() => setStyle(s)} /> {s === 'PIN' ? 'Pin' : 'Token'}
              </label>
            ))}
          </fieldset>
        </>
      )}
      {region && (
        <>
          <label className="check">
            <input type="checkbox" checked={ownColor !== null} onChange={(e) => setOwnColor(e.currentTarget.checked ? color : null)} /> Own colour
          </label>
          {ownColor !== null && <ColorField label="Region colour" value={ownColor} swatches={MAP_SWATCHES} onChange={(hex) => {
                setOwnColor(hex);
                setSaved(false);
              }} />}
          <RegionMeasures region={region} scale={view.map.scale} units={units} />
        </>
      )}
      <div className="actions">
        <button type="submit" className="primary">
          Save
        </button>
        <button type="button" onClick={onCenter}>
          Center
        </button>
        <button type="button" className="quiet" onClick={onDelete}>
          Delete…
        </button>
      </div>
      {saved && (
        <p className="meta" role="status">
          Saved
        </p>
      )}
    </form>
  );
}

/** Manage categories…: every category with Edit and Delete, and + New category (`showCategoryManager`). */
function CategoryManager({
  file,
  view,
  icons,
  pictureUrl,
  run,
  onClose,
}: {
  file: string;
  view: AtlasMapView;
  icons: ReadonlyMap<string, MapIcon>;
  pictureUrl: PictureUrl;
  run: (work: () => Promise<unknown>, fallback: string) => Promise<boolean>;
  onClose: () => void;
}) {
  const [editing, setEditing] = useState<string | 'new' | null>(view.categories.length === 0 ? 'new' : null);
  const [deleting, setDeleting] = useState<{ id: string; text: string } | null>(null);
  async function askDelete(c: MarkerCategory) {
    const remove = () => run(() => realms.deleteCategory(file, c.id).then(() => setDeleting(null)), 'The category could not be deleted.');
    if (!settings()['general.confirmDeletes']) return void remove();
    const used = await realms.categoryUse(file, c.id);
    setDeleting({ id: c.id, text: `Delete “${c.name}”? ${used === 0 ? 'Nothing uses it.' : `Its ${plural(used, 'pin or region keeps', 'pins and regions keep')} existing, uncategorized.`}` });
  }
  return (
    <section className="panel atlas-categories" aria-label="Categories">
      <h2>Categories</h2>
      <p className="meta">A category is a layer on every map: its pins and regions share a colour and icon, and can be shown or hidden together from the Legend.</p>
      {view.categories.length === 0 && <p className="meta">No categories yet.</p>}
      <ul>
        {view.categories.map((c) =>
          editing === c.id ? (
            <li key={c.id}>
              <CategoryForm
                initial={c}
                icons={icons}
                iconList={view.icons}
                pictureUrl={pictureUrl}
                onCancel={() => setEditing(null)}
                onSave={(details) => run(() => realms.updateCategory(file, c.id, details), 'The category could not be saved.').then((ok) => ok && setEditing(null))}
              />
            </li>
          ) : (
            <li key={c.id} className="atlas-category-item">
              <CategoryBadge category={c} icons={icons} pictureUrl={pictureUrl} size={22} />
              <span>{c.name}</span>
              <button type="button" onClick={() => setEditing(c.id)}>
                Edit
              </button>
              <button type="button" className="quiet" onClick={() => void askDelete(c)}>
                Delete…
              </button>
              {deleting?.id === c.id && (
                <Confirm label={`Delete ${c.name}`} text={deleting.text} button="Delete category" onCancel={() => setDeleting(null)} onConfirm={() => run(() => realms.deleteCategory(file, c.id).then(() => setDeleting(null)), 'The category could not be deleted.')} />
              )}
            </li>
          ),
        )}
      </ul>
      {editing === 'new' ? (
        <CategoryForm
          icons={icons}
          iconList={view.icons}
          pictureUrl={pictureUrl}
          onCancel={() => setEditing(null)}
          onSave={(details) => run(() => realms.createCategory(file, details), 'The category could not be made.').then((ok) => ok && setEditing(null))}
        />
      ) : (
        <div className="actions">
          <button type="button" onClick={() => setEditing('new')}>
            + New category
          </button>
          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>
      )}
    </section>
  );
}

function CategoryForm({
  initial,
  icons,
  iconList,
  pictureUrl,
  onSave,
  onCancel,
}: {
  initial?: MarkerCategory;
  icons: ReadonlyMap<string, MapIcon>;
  iconList: readonly MapIcon[];
  pictureUrl: PictureUrl;
  onSave: (details: CategoryDetails) => Promise<unknown>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [colorHex, setColorHex] = useState(initial && /^#[0-9A-Fa-f]{6}$/.test(initial.colorHex) ? initial.colorHex : NEW_CATEGORY_COLOR);
  const [icon, setIcon] = useState(initial?.icon ?? '');
  const [choosing, setChoosing] = useState(false);
  return (
    <form
      className="panel"
      aria-label={initial ? 'Edit category' : 'New category'}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        void onSave({ name, colorHex, icon });
      }}
    >
      <label>
        Name
        <input value={name} required maxLength={MAP_EDIT_LIMITS.label} autoFocus placeholder="e.g. City, Dungeon, Landmark" onChange={(e) => setName(e.currentTarget.value)} />
      </label>
      <ColorField label="Colour" value={colorHex} swatches={MAP_SWATCHES} onChange={setColorHex} />
      <div className="atlas-icon-row">
        <span>Icon</span>
        <IconSwatch value={icon} color={colorHex} icons={icons} pictureUrl={pictureUrl} />
        <button type="button" onClick={() => setChoosing(!choosing)}>
          Choose icon…
        </button>
      </div>
      {choosing && (
        <IconPicker
          value={icon}
          forPin={false}
          color={colorHex}
          icons={iconList}
          pictureUrl={pictureUrl}
          onCancel={() => setChoosing(false)}
          onPick={(chosen) => {
            setIcon(chosen);
            setChoosing(false);
          }}
        />
      )}
      <div className="actions">
        <button type="submit" className="primary">
          {initial ? 'Save' : 'Create'}
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function Confirm({ label, text, button, onConfirm, onCancel }: { label: string; text: ReactNode; button: string; onConfirm: () => Promise<unknown>; onCancel: () => void }) {
  return (
    <div className="panel confirm" role="alertdialog" aria-label={label}>
      <p>{text}</p>
      <div className="actions">
        <button type="button" className="danger" onClick={() => void onConfirm()}>
          {button}
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

